import { TableName } from '../../../../../tables'
import { UIPermission, DetailPanel, DetailFieldProps, FieldType, FieldValue, Action, Tab, useGetCrumbs, useUIActions, ModalId } from '../../../../../features/ui'
import { Customer, SaleOrder, SalePaymentItem } from '../../../state/types'
import { useEffect, useMemo, useState } from 'react'
import { OperationItemsTable, PriceBreakdown, usePriceOutlineHooks, PaymentStatus, operationHelpers } from '../../../../operations'
import { CompanyUserName, useSystemStateHooks } from '../../../../system'
import { AdjustmentNote, useEmployeeLoaders, usePaymentTypeLoaders, PaymentCategory, Check, useEmployeeHooks, CreditPayment, InvoiceRequestedModal, InvoiceStatusField } from '../../../../administration'
import { RoutePath } from '../../../../../features/routes'
import { DirectArchiveModal, Labels, useDataTypesHooks, useDescribeHooks } from '../../../../../features/data-types'
import { useLocation, useNavigate } from 'react-router-dom'
import { saleHelpers } from '../../../hooks/saleHooks'
import { SalePaymentItemsTable } from '../../sale-operation/sale-payment-items-table/salePaymentItemsTable'
import { UploadSaleReceiptFormModal } from '../../sale-operation/upload-sale-receipt-modal/uploadSaleReceiptFormModal'
import { CustomerLink } from '../../customer/customer-link/customerLink'
import { saleOperationHelpers, useSaleOperationHooks } from '../../../hooks/saleOperationHooks'
import { OperationCreditPaymentsTable } from '../../sale-operation/operation-credit-payments-table/operationCreditPaymentsTable'
import { Delivery, DeliveryOrder, DeliveryOrdersDataList, DownloadDeliveryNoteModal, deliveryOrderHelpers, UploadDeliveryNoteModal, useDeliveryHooks, DeliveryStatus } from '../../../../deliveries'
import { SaleOperationDocumentsPanel } from '../../sale-operation/documents-panel/saleOperationDocumentsPanel'
import { FiscalDocument, FiscalDocumentStatus, getLatestInvoice, isUninvoicedFiscalDocumentStatus } from '../../../../fiscal-documents'
import { DownloadSaleFilesModal } from '../../sale-operation/download-sale-files-modal/downloadSaleFilesModal'
import { DownloadReceiptModal } from '../../../../common'
import { AddNoteModal } from '../../sale-operation/notes/addNoteModal'
import { NotesTabContent } from '../../sale-operation/notes/notesTabContent'
import { useSaleActions } from '../../../actions/saleActions'
import { useAddNote } from '../../../hooks/notesHooks'

type Props = {
  sale: SaleOrder
  deliveryOrders: DeliveryOrder[]
  deliveries: Delivery[]
  checks: Check[]
  customer?: Customer
  creditPayments: CreditPayment[]
  fiscalDocuments: FiscalDocument[]
  adjustmentNotes: AdjustmentNote[]
  onRefreshDeliveries?: () => void
  onRefreshFiscalDocuments?: () => Promise<void>
  isAutoRefreshExhausted?: boolean
}

export const SaleDetail = (props: Props) => {
  const { sale, deliveryOrders, checks, customer, creditPayments, fiscalDocuments, adjustmentNotes, onRefreshDeliveries, onRefreshFiscalDocuments, isAutoRefreshExhausted = false } = props
  const { items, paymentItems, events, perceptions, deliveryData, internalNotes: _, ...data } = sale

  const [deliveries, setDeliveries] = useState<Delivery[]>(props.deliveries)
  
  const [currentPaymentItem, setCurrentPaymentItem] = useState<SalePaymentItem>()
  const [isSaleEditable, setIsSaleEditable] = useState<boolean>()
  const [invoice, setInvoice] = useState<FiscalDocument>()
  const [currentDelivery, setCurrentDelivery] = useState<Delivery>()

  const { isInvoiceable } = saleHelpers
  const { isArchivable, sortPaymentItems } = saleOperationHelpers
  const { describeCode } = operationHelpers
  const { isEditable, isDeliveryOrderEmitable, isManualCreditNoteAllowed } = useSaleOperationHooks()
  const { userHasPermission } = useSystemStateHooks()
  const { formatDateTime } = useDataTypesHooks()
  const { refreshDeliveries } = useDeliveryHooks()
  const { getPriceOutline } = usePriceOutlineHooks()
  const { describe } = useDescribeHooks()
  const { describeEmployee } = useEmployeeHooks()
  const navigate = useNavigate()
  const locationFrom = useLocation().state?.from

  const employeeLoaders = useEmployeeLoaders()
  const paymentTypeLoaders = usePaymentTypeLoaders()

  const { openModal } = useUIActions()
  const { saveSale } = useSaleActions()

  const handleAddNote = useAddNote(sale, async (updated: SaleOrder) => {
    await saveSale(updated)
  })

  const readOnly = !userHasPermission(UIPermission.SAVE_SALES) || sale.cancelled || sale.archived
  const hasDeliveryPermission = userHasPermission(UIPermission.SAVE_SALE_DELIVERIES)
  const hasInvoicePermission = userHasPermission(UIPermission.INVOICE_SALES)
  const canAnnulInvoice = hasInvoicePermission && !sale.cancelled && !sale.archived
  const deliveriesReadOnly = !hasDeliveryPermission || !!sale.cancelled || !!sale.archived

  const load = async () => {
    const loadedIsEditable = await isEditable(sale)
    setIsSaleEditable(loadedIsEditable)
  }

  // La factura se deriva de los documentos cargados: al refrescar, el invoiceStatus denormalizado de la
  // operación puede llegar después, así que la fuente para el badge/botón es esta lista recargada.
  // Se ignora el borrador y las facturas anuladas: la factura anulada no es la factura activa.
  useEffect(() => {
    setInvoice(getLatestInvoice(fiscalDocuments))
  }, [fiscalDocuments])

  useEffect(() => {
    load()
  }, [sale])

  useEffect(() => {
    setDeliveries(props.deliveries)
  }, [props.deliveries])

  const title = useMemo(() => describe(TableName.SALE_ORDERS, sale), [sale])
  const fromCreditAccounts = locationFrom === RoutePath.CREDIT_ACCOUNTS
  const crumbs = useGetCrumbs(
    fromCreditAccounts ? 'Cuentas Corrientes' : 'Ventas',
    locationFrom || RoutePath.SALES,
    fromCreditAccounts ? title : describeCode(sale)
  )

  const findDeliveryOrder = (delivery?: Delivery) => deliveryOrders.find(order => order.id === delivery?.orderId)

  const paymentType = useMemo(() => paymentTypeLoaders().findPaymentType(sale.paymentTypeId), [sale.paymentTypeId])
  const sortedPaymentItems = useMemo(() => sortPaymentItems(sale), [sale])
  const isCreditPayment = useMemo(() => paymentType?.category === PaymentCategory.CREDIT_ACCOUNT, [paymentType])
  const isReturnable = useMemo(() => deliveryOrders.some(deliveryOrder => deliveryOrderHelpers.isReturnable(deliveryOrder)), [deliveryOrders])
  const isCancellable = useMemo(() => deliveryOrderHelpers.isCancellable(sale, deliveryOrders), [sale, deliveryOrders])
  const isInvoiceAuthorized = useMemo(() => invoice?.status === FiscalDocumentStatus.AUTHORIZED, [invoice?.status])
  const currentDeliveryOrder = useMemo(() => findDeliveryOrder(currentDelivery), [currentDelivery])

  const onDownloadReceipt = (index: string) => {
    setCurrentPaymentItem(sortedPaymentItems.find((_, i) => i.toString() === index)!)
    openModal(ModalId.DOWNLOAD_RECEIPT)
  }

  const onUploadReceipt = (index: string) => {
    setCurrentPaymentItem(sortedPaymentItems.find((_, i) => i.toString() === index)!)
    openModal(ModalId.UPLOAD_SALE_RECEIPT)
  }

  const fields: DetailFieldProps[] = [
    {
      name: 'creationDate',
      type: FieldType.DATE,
      label: 'Fecha',
      render: () => <>{formatDateTime(new Date(sale.creationDate))}</>
    },
    {
      name: 'sellerId',
      type: FieldType.TABLE,
      table: TableName.EMPLOYEES,
      label: 'Vendedor',
      render: (value?: FieldValue) => {
        const seller = employeeLoaders().findEmployee(value! as string)!
        return <>{describeEmployee(seller)}</>
      }
    },
    {
      name: 'customerId',
      type: FieldType.TABLE,
      table: TableName.CUSTOMERS,
      label: 'Cliente',
      loading: !customer,
      render: () => <CustomerLink customer={customer!} />
    },
    {
      name: 'customerTypeId',
      type: FieldType.TABLE,
      table: TableName.CUSTOMER_TYPES,
      label: 'Lista de Precios'
    },
    {
      name: 'items',
      type: FieldType.TABLE,
      render: () => (
        <OperationItemsTable
          itemsData={items}
          createMode={false}
          readOnly
        />
      )
    },
    {
      name: 'priceBreakdown',
      type: FieldType.PRICE,
      label: 'Precio',
      render: () => {
        const priceOutline = getPriceOutline(TableName.SALE_ORDERS, sale)
        return <PriceBreakdown data={priceOutline} />
      }
    },
    {
      name: 'paymentStatus',
      type: FieldType.TEXT,
      label: 'Estado de Pago',
      render: (value?: FieldValue) => <>{Labels.paymentStatusLabel(value! as PaymentStatus)}</>
    },
    {
      name: 'invoiceStatus',
      type: FieldType.TEXT,
      label: 'Facturación',
      render: () => <InvoiceStatusField
        invoices={invoice ? [invoice] : []}
        status={sale.invoiceStatus}
        onRefresh={async () => { await onRefreshFiscalDocuments?.() }}
        onRegenerate={() => navigate(RoutePath.INVOICE)}
        isAutoRefreshExhausted={isAutoRefreshExhausted}
      />
    },
    {
      name: 'deliveryStatus',
      type: FieldType.TEXT,
      label: 'Estado de Entrega',
      render: (value?: FieldValue) => <>{Labels.deliveryStatusLabel(value! as DeliveryStatus)}</>
    }
  ]

  if (sale.paymentStatus === PaymentStatus.PARTIALLY_PAYED) {
    const paidAmountField = {
      name: 'paidAmount',
      type: FieldType.PRICE,
      label: 'Monto Pagado'
    }
    fields.splice(6, 0, paidAmountField)
  }
  if (!isCreditPayment && sortedPaymentItems.length > 0) {
    const paymentItemsField = {
      name: 'paymentItems',
      type: FieldType.TABLE,
      render: () => (
        <SalePaymentItemsTable
          salePaymentItems={sortedPaymentItems.map((paymentItem, index) => ({
            index: index.toString(),
            paymentTypeId: paymentItem.paymentTypeId,
            creditInFavor: paymentItem.creditInFavor,
            isRounding: paymentItem.isRounding,
            cashFundId: paymentItem.destinationId,
            amount: paymentItem.amount,
            checkName: checks.find(check => check.id === paymentItem.destinationId)?.name,
            receiptUrl: paymentItem.receiptUrl
          }))}
          createMode={false}
          readOnly={readOnly}
          onDownloadReceipt={onDownloadReceipt}
          onUploadReceipt={onUploadReceipt}
        />
      )
    }
    fields.splice(5, 0, paymentItemsField)
  }
  if (isCreditPayment && creditPayments.length > 0) {
    const creditPaymentsField = {
      name: 'creditPayments',
      type: FieldType.TABLE,
      render: () => (
        <OperationCreditPaymentsTable
          operationId={sale.id}
          creditPayments={creditPayments}
        />
      )
    }
    fields.splice(5, 0, creditPaymentsField)
  }
  if (isCreditPayment || sortedPaymentItems.length === 0) {
    const paymentTypeField = {
      name: 'paymentTypeId',
      type: FieldType.TABLE,
      table: TableName.PAYMENT_TYPES,
      label: 'Medio de Pago'
    }
    fields.splice(5, 0, paymentTypeField)
  }
  if (sale.notes) {
    const notesField = {
      name: 'notes',
      type: FieldType.TEXT,
      label: 'Notas'
    }
    fields.push(notesField)
  }
  if (sale.negativeStateUserId) {
    const negativeStateLabel = sale.cancelled ? 'Cancelado por' : 'Archivado por'
    fields.push({
      name: 'negativeStateUserId',
      type: FieldType.TEXT,
      label: negativeStateLabel,
      render: () => {
        const dateStr = sale.negativeStateAt ? ` - ${formatDateTime(new Date(sale.negativeStateAt))}` : ''
        return <><CompanyUserName userId={sale.negativeStateUserId!} />{dateStr}</>
      }
    })
  }

  const onDownloadFiles = () => openModal(ModalId.DOWNLOAD_FILES)

  const onEdit = () => navigate(RoutePath.EDIT)

  const onEmitDelivery = () => navigate(RoutePath.EMIT_DELIVERY)

  const onConfirmPayment = () => navigate(RoutePath.CONFIRM_PAYMENT)

  const onInvoice = () => navigate(RoutePath.INVOICE)

  const onReturnItems = () => navigate(RoutePath.REGISTER_RETURN)

  const onCancelItems = () => navigate(RoutePath.CANCEL_ITEMS)

  const onArchive = () => openModal(ModalId.DIRECT_ARCHIVE)

  const onManualCreditNote = () => navigate(RoutePath.MANUAL_CREDIT_NOTE)

  const actions: Action[] = useMemo(() => {
    const actionList: Action[] = [
      { label: 'Documentos', kind: 'tertiary', icon: 'download', onClick: onDownloadFiles }
    ]
    if (!readOnly && isManualCreditNoteAllowed(TableName.SALE_ORDERS, sale, isSaleEditable !== false)) {
      actionList.push({ label: 'Nota de Crédito', icon: 'file-earmark-minus', kind: 'quinary', onClick: onManualCreditNote })
    }

    const isPayed = sale.paymentStatus === PaymentStatus.PAYED
    if (!readOnly) {
      if (isSaleEditable) {
        actionList.push({ label: 'Editar Venta', icon: 'pencil-fill', onClick: onEdit })
      }
      if (!isCreditPayment && !isPayed) {
        actionList.push({ label: 'Confirmar Pago', icon: 'check-lg', onClick: onConfirmPayment })
      }
    }
    if (hasDeliveryPermission) {
      if (isDeliveryOrderEmitable(sale)) {
        actionList.push({ label: 'Emitir Entrega', icon: 'calendar-range', onClick: onEmitDelivery })
      }
      if (isReturnable) {
        actionList.push({ label: 'Registrar Devolución', icon: 'arrow-return-left', onClick: onReturnItems })
      }
    }
    if (hasInvoicePermission && isInvoiceable(sale) && isUninvoicedFiscalDocumentStatus(invoice?.status)) {
      actionList.push({ label: 'Facturar', icon: 'file-earmark-check', kind: 'quinary', onClick: onInvoice })
    }
    if (!deliveriesReadOnly && isCancellable) {
      actionList.push({ label: 'Cancelar Productos', icon: 'x-circle', kind: 'secondary', onClick: onCancelItems })
    }
    if (!readOnly && isArchivable(sale)) {
      actionList.push({ label: 'Archivar Venta', icon: 'archive-fill', kind: 'secondary', onClick: onArchive })
    }

    return actionList
  }, [sale, paymentType, isSaleEditable, isInvoiceAuthorized, isReturnable, isCancellable, invoice?.status])

  const tabs = useMemo(() => {
    const tabList: Tab[] = [
      {
        id: 'documents',
        title: 'Documentos',
        content: (
          <SaleOperationDocumentsPanel
            table={TableName.SALE_ORDERS}
            operation={sale}
            deliveries={deliveries}
            deliveryOrders={deliveryOrders}
            creditPayments={creditPayments}
            fiscalDocuments={fiscalDocuments}
            adjustmentNotes={adjustmentNotes}
            deliveriesReadOnly={deliveriesReadOnly}
            canAnnulInvoice={canAnnulInvoice}
            onDownloadDeliveryNote={setCurrentDelivery}
            onRefreshDeliveries={onRefreshDeliveries}
            onRefreshFiscalDocuments={onRefreshFiscalDocuments}
            isAutoRefreshExhausted={isAutoRefreshExhausted}
          />
        )
      }
    ]
    tabList.push({
      id: 'deliveryOrders',
      title: 'Entregas',
      content: <DeliveryOrdersDataList
        deliveryData={deliveryData}
        deliveryOrders={deliveryOrders}
        deliveries={deliveries}
        operation={sale}
        customer={customer}
        branchId={sale.branchId}
        readOnly={deliveriesReadOnly}
        hideDeliveries
        onChangeCurrentDelivery={setCurrentDelivery}
        onRefresh={onRefreshDeliveries}
      />
    })
    tabList.push({
      id: 'notes',
      title: 'Notas',
      content: <NotesTabContent 
        originalNote={sale.notes} 
        originalDate={sale.creationDate}
        internalNotes={sale.internalNotes || []} 
      />
    })

    return tabList
  }, [deliveryData, deliveryOrders, deliveries, items, customer, sale, creditPayments, fiscalDocuments, adjustmentNotes, isInvoiceAuthorized, hasDeliveryPermission, canAnnulInvoice, onRefreshDeliveries, sale.notes, sale.internalNotes, isAutoRefreshExhausted])

  const onUploadDeliveryNote = async () => {
    const refreshedDeliveries = await refreshDeliveries(deliveries)
    setDeliveries(refreshedDeliveries)
  }

  return (<>
    <DetailPanel
      tableName={TableName.SALE_ORDERS}
      title={title}
      crumbs={crumbs}
      fields={fields}
      data={data}
      icon='tags'
      imgSpace={3}
      tabs={tabs}
      readOnly={readOnly}
      actions={actions}
    />
    <DownloadSaleFilesModal
      table={TableName.SALE_ORDERS}
      saleOperation={sale}
    />
    <DownloadReceiptModal operation={currentPaymentItem} />
    <DownloadDeliveryNoteModal
      delivery={currentDelivery}
      deliveryOrder={currentDeliveryOrder}
    />
    <UploadSaleReceiptFormModal
      table={TableName.SALE_ORDERS}
      operation={sale}
      paymentItem={currentPaymentItem}
    />
    <UploadDeliveryNoteModal
      delivery={currentDelivery}
      deliveryOrder={currentDeliveryOrder}
      onUpload={onUploadDeliveryNote}
    />
    {invoice && (
      <InvoiceRequestedModal
        operationTable={TableName.SALE_ORDERS}
        fiscalDocument={invoice}
      />
    )}
    <DirectArchiveModal
      tableName={TableName.SALE_ORDERS}
      dataType={sale}
    />
    <AddNoteModal onSubmit={handleAddNote} />
  </>)
}
